import React, { useState } from 'react';
import { TrendingUpIcon, AlertCircleIcon, CheckCircleIcon, ClockIcon, ActivityIcon, UsersIcon, TargetIcon, ZapIcon, DollarSignIcon, PlayIcon, PauseIcon, FolderIcon, BookOpenIcon, SparklesIcon, ArrowUpIcon, ArrowDownIcon, BrainIcon, CalendarIcon, AlertTriangleIcon } from 'lucide-react';
export function DashboardPage() {
  const [activeTab, setActiveTab] = useState<'executive' | 'activity'>('executive');
  const topMetrics = [{
    label: 'Pipeline (QTD)',
    value: '$8.3M',
    target: '$10.0M',
    change: '+18%',
    trend: 'up',
    color: 'green'
  }, {
    label: 'Revenue (QTD)',
    value: '$4.5M',
    target: '$5.0M',
    change: '+12%',
    trend: 'up',
    color: 'blue'
  }, {
    label: 'Active Programs',
    value: '8',
    target: '10',
    change: '+2',
    trend: 'up',
    color: 'purple'
  }, {
    label: 'Plays Running',
    value: '24',
    target: '30',
    change: '+6',
    trend: 'up',
    color: 'orange'
  }, {
    label: 'Accounts Engaged',
    value: '1,247',
    change: '+156',
    trend: 'up',
    color: 'cyan'
  }, {
    label: 'Win Rate',
    value: '24.5%',
    change: '-2%',
    trend: 'down',
    color: 'red'
  }];
  const recentItems = [{
    type: 'program',
    name: 'Q1 Enterprise ABM Campaign',
    lastAccessed: '2 minutes ago',
    status: 'active'
  }, {
    type: 'playbook',
    name: 'High-Intent Outreach Playbook',
    lastAccessed: '15 minutes ago',
    status: 'active'
  }, {
    type: 'play',
    name: 'Executive Nurture Sequence',
    lastAccessed: '1 hour ago',
    status: 'draft'
  }];
  const integrationHealth = [{
    name: 'Salesforce',
    status: 'healthy',
    lastSync: '2m ago'
  }, {
    name: 'HubSpot',
    status: 'healthy',
    lastSync: '5m ago'
  }, {
    name: 'ZoomInfo',
    status: 'warning',
    lastSync: '45m ago'
  }];
  const activePrograms = [{
    name: 'Q1 Enterprise Growth',
    pipeline: '$5.2M',
    spend: '$450K',
    roi: '3.2x',
    health: 'healthy',
    progress: 75
  }, {
    name: 'PLG Activation',
    pipeline: '$3.1M',
    spend: '$280K',
    roi: '2.8x',
    health: 'at-risk',
    progress: 62
  }];
  const playExecution = [{
    name: 'High-Intent Account Blitz',
    status: 'running',
    accounts: 342,
    touchpoints: 2847,
    responses: 156,
    responseRate: '5.5%'
  }, {
    name: 'PLG Activation Flow',
    status: 'running',
    accounts: 189,
    touchpoints: 1523,
    responses: 89,
    responseRate: '5.8%'
  }, {
    name: 'Executive Nurture',
    status: 'paused',
    accounts: 78,
    touchpoints: 234,
    responses: 23,
    responseRate: '9.8%'
  }];
  const teamActivity = [{
    user: 'Sarah Chen',
    action: 'launched play',
    target: 'High-Intent Account Blitz',
    time: '5 min ago'
  }, {
    user: 'Mike Rodriguez',
    action: 'updated program',
    target: 'Q1 Enterprise Growth',
    time: '23 min ago'
  }, {
    user: 'Lisa Park',
    action: 'created playbook',
    target: 'Partner Outreach',
    time: '1 hour ago'
  }];
  const upcomingActions = [{
    title: 'Q1 Planning Review',
    dueDate: 'Tomorrow, 2:00 PM',
    type: 'meeting',
    priority: 'high'
  }, {
    title: 'Program Budget Approval',
    dueDate: 'Jan 28',
    type: 'approval',
    priority: 'high'
  }, {
    title: 'Play Performance Review',
    dueDate: 'Jan 30',
    type: 'review',
    priority: 'medium'
  }];
  const aiInsights = [{
    type: 'opportunity',
    message: 'High-intent spike detected in 47 accounts',
    action: 'Launch targeted play',
    priority: 'high'
  }, {
    type: 'warning',
    message: 'PLG Activation conversion down 8%',
    action: 'Review messaging',
    priority: 'medium'
  }, {
    type: 'success',
    message: 'Enterprise segment exceeding targets by 12%',
    action: 'Scale investment',
    priority: 'low'
  }];
  const activityFeed = [{
    action: 'Program launched',
    details: 'Q1 Enterprise ABM Campaign',
    time: '5 minutes ago',
    icon: TargetIcon,
    color: 'blue'
  }, {
    action: 'Playbook updated',
    details: 'High-Intent Outreach Playbook',
    time: '23 minutes ago',
    icon: ActivityIcon,
    color: 'purple'
  }, {
    action: 'Integration synced',
    details: 'Salesforce - 342 records updated',
    time: '1 hour ago',
    icon: ZapIcon,
    color: 'green'
  }, {
    action: 'Team member added',
    details: 'Sarah Chen joined the workspace',
    time: '2 hours ago',
    icon: UsersIcon,
    color: 'cyan'
  }];
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-4581">
      <div className="max-w-[1800px] mx-auto px-6 py-8" data-id="element-4582">
        {/* Header with subtle fade-in animation */}
        <div className="mb-6 animate-fade-in" data-id="element-4583">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-4584">
            GTM Command Center
          </h1>
          <p className="text-gray-600" data-id="element-4585">
            Real-time visibility across all GTM operations
          </p>
        </div>
        {/* Top Metrics Bar with staggered animation */}
        <div className="grid grid-cols-6 gap-4 mb-6" data-id="element-4586">
          {topMetrics.map((metric, index) => {
          const TrendIcon = metric.trend === 'up' ? ArrowUpIcon : ArrowDownIcon;
          return <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-all duration-300 hover:-translate-y-1" style={{
            animation: `slideUp 0.5s ease-out ${index * 0.1}s both`
          }} data-id="element-4587">
                <p className="text-xs text-gray-600 mb-1" data-id="element-4588">{metric.label}</p>
                <div className="flex items-end justify-between mb-1" data-id="element-4589">
                  <p className="text-2xl font-bold text-gray-900" data-id="element-4590">
                    {metric.value}
                  </p>
                  <div className={`flex items-center gap-1 text-xs font-medium ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`} data-id="element-4591">
                    <TrendIcon className="w-3 h-3" data-id="element-4592" />
                    {metric.change}
                  </div>
                </div>
                {metric.target && <p className="text-xs text-gray-500" data-id="element-4593">
                    Target: {metric.target}
                  </p>}
              </div>;
        })}
        </div>
        {/* Main Grid with fade-in */}
        <div className="grid grid-cols-12 gap-6 animate-fade-in-slow" data-id="element-4594">
          {/* Left Column - 4 cols */}
          <div className="col-span-4 space-y-6" data-id="element-4595">
            {/* Recently Touched with hover effect */}
            <div className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-300" data-id="element-4596">
              <div className="px-4 py-3 border-b border-gray-200" data-id="element-4597">
                <div className="flex items-center gap-2" data-id="element-4598">
                  <ClockIcon className="w-4 h-4 text-gray-600" data-id="element-4599" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-4600">
                    Recently Touched
                  </h3>
                </div>
              </div>
              <div className="divide-y divide-gray-200" data-id="element-4601">
                {recentItems.map((item, index) => <div key={index} className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-4602">
                    <div className="flex items-center justify-between mb-1" data-id="element-4603">
                      <p className="text-sm font-medium text-gray-900" data-id="element-4604">
                        {item.name}
                      </p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${item.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-4605">
                        {item.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-4606">
                      <span className="capitalize" data-id="element-4607">{item.type}</span>
                      <span data-id="element-4608">•</span>
                      <span data-id="element-4609">{item.lastAccessed}</span>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Integration Health with pulse animation */}
            <div className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-300" data-id="element-4610">
              <div className="px-4 py-3 border-b border-gray-200" data-id="element-4611">
                <div className="flex items-center gap-2" data-id="element-4612">
                  <ActivityIcon className="w-4 h-4 text-gray-600" data-id="element-4613" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-4614">
                    Integration Health
                  </h3>
                </div>
              </div>
              <div className="p-4 space-y-3" data-id="element-4615">
                {integrationHealth.map((integration, index) => <div key={index} className="flex items-center justify-between" data-id="element-4616">
                    <div className="flex items-center gap-2" data-id="element-4617">
                      {integration.status === 'healthy' ? <CheckCircleIcon className="w-4 h-4 text-green-600 animate-pulse-slow" data-id="element-4618" /> : <AlertCircleIcon className="w-4 h-4 text-orange-600 animate-pulse" data-id="element-4619" />}
                      <div data-id="element-4620">
                        <p className="text-sm font-medium text-gray-900" data-id="element-4621">
                          {integration.name}
                        </p>
                        <p className="text-xs text-gray-600" data-id="element-4622">
                          Last sync: {integration.lastSync}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${integration.status === 'healthy' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-4623">
                      {integration.status}
                    </span>
                  </div>)}
              </div>
            </div>
            {/* Upcoming Actions */}
            <div className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-300" data-id="element-4624">
              <div className="px-4 py-3 border-b border-gray-200" data-id="element-4625">
                <div className="flex items-center gap-2" data-id="element-4626">
                  <CalendarIcon className="w-4 h-4 text-gray-600" data-id="element-4627" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-4628">
                    Upcoming Actions
                  </h3>
                </div>
              </div>
              <div className="divide-y divide-gray-200" data-id="element-4629">
                {upcomingActions.map((action, index) => <div key={index} className="px-4 py-3 hover:bg-gray-50 transition-colors" data-id="element-4630">
                    <div className="flex items-start justify-between mb-1" data-id="element-4631">
                      <p className="text-sm font-medium text-gray-900" data-id="element-4632">
                        {action.title}
                      </p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${action.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-4633">
                        {action.priority}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-4634">
                      <span className="capitalize" data-id="element-4635">{action.type}</span>
                      <span data-id="element-4636">•</span>
                      <span data-id="element-4637">{action.dueDate}</span>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Team Activity */}
            <div className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-300" data-id="element-4638">
              <div className="px-4 py-3 border-b border-gray-200" data-id="element-4639">
                <div className="flex items-center gap-2" data-id="element-4640">
                  <UsersIcon className="w-4 h-4 text-gray-600" data-id="element-4641" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-4642">
                    Team Activity
                  </h3>
                </div>
              </div>
              <div className="divide-y divide-gray-200" data-id="element-4643">
                {teamActivity.map((activity, index) => <div key={index} className="px-4 py-3" data-id="element-4644">
                    <p className="text-sm text-gray-900 mb-1" data-id="element-4645">
                      <span className="font-semibold" data-id="element-4646">{activity.user}</span>{' '}
                      {activity.action}{' '}
                      <span className="font-medium" data-id="element-4647">{activity.target}</span>
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-4648">{activity.time}</p>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Middle Column - 5 cols */}
          <div className="col-span-5 space-y-6" data-id="element-4649">
            {/* Active Programs with scale hover */}
            <div className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-300" data-id="element-4650">
              <div className="px-4 py-3 border-b border-gray-200" data-id="element-4651">
                <div className="flex items-center justify-between" data-id="element-4652">
                  <div className="flex items-center gap-2" data-id="element-4653">
                    <FolderIcon className="w-4 h-4 text-gray-600" data-id="element-4654" />
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-4655">
                      Active Programs
                    </h3>
                  </div>
                  <button className="text-xs text-red-600 hover:text-red-700 font-medium" data-id="element-4656">
                    View all
                  </button>
                </div>
              </div>
              <div className="p-4 space-y-3" data-id="element-4657">
                {activePrograms.map((program, index) => <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-300" data-id="element-4658">
                    <div className="flex items-center justify-between mb-3" data-id="element-4659">
                      <h4 className="text-sm font-semibold text-gray-900" data-id="element-4660">
                        {program.name}
                      </h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${program.health === 'healthy' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-4661">
                        {program.health}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-3 mb-3" data-id="element-4662">
                      <div data-id="element-4663">
                        <p className="text-xs text-gray-600 mb-1" data-id="element-4664">Pipeline</p>
                        <p className="text-sm font-bold text-gray-900" data-id="element-4665">
                          {program.pipeline}
                        </p>
                      </div>
                      <div data-id="element-4666">
                        <p className="text-xs text-gray-600 mb-1" data-id="element-4667">Spend</p>
                        <p className="text-sm font-bold text-gray-900" data-id="element-4668">
                          {program.spend}
                        </p>
                      </div>
                      <div data-id="element-4669">
                        <p className="text-xs text-gray-600 mb-1" data-id="element-4670">ROI</p>
                        <p className="text-sm font-bold text-green-600" data-id="element-4671">
                          {program.roi}
                        </p>
                      </div>
                      <div data-id="element-4672">
                        <p className="text-xs text-gray-600 mb-1" data-id="element-4673">Progress</p>
                        <p className="text-sm font-bold text-gray-900" data-id="element-4674">
                          {program.progress}%
                        </p>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-4675">
                      <div className={`h-full rounded-full transition-all duration-500 ${program.health === 'healthy' ? 'bg-green-600' : 'bg-yellow-600'}`} style={{
                    width: `${program.progress}%`
                  }} data-id="element-4676" />
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Play Execution Status */}
            <div className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-300" data-id="element-4677">
              <div className="px-4 py-3 border-b border-gray-200" data-id="element-4678">
                <div className="flex items-center justify-between" data-id="element-4679">
                  <div className="flex items-center gap-2" data-id="element-4680">
                    <SparklesIcon className="w-4 h-4 text-gray-600" data-id="element-4681" />
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-4682">
                      Play Execution Status
                    </h3>
                  </div>
                  <button className="text-xs text-red-600 hover:text-red-700 font-medium" data-id="element-4683">
                    View all
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-4684">
                <table className="w-full" data-id="element-4685">
                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-4686">
                    <tr data-id="element-4687">
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700" data-id="element-4688">
                        Play Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700" data-id="element-4689">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700" data-id="element-4690">
                        Accounts
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700" data-id="element-4691">
                        Touchpoints
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700" data-id="element-4692">
                        Response Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-4693">
                    {playExecution.map((play, index) => <tr key={index} className="hover:bg-gray-50 transition-colors" data-id="element-4694">
                        <td className="px-4 py-3" data-id="element-4695">
                          <p className="text-sm font-medium text-gray-900" data-id="element-4696">
                            {play.name}
                          </p>
                        </td>
                        <td className="px-4 py-3" data-id="element-4697">
                          <div className="flex items-center gap-2" data-id="element-4698">
                            {play.status === 'running' ? <PlayIcon className="w-3 h-3 text-green-600" data-id="element-4699" /> : <PauseIcon className="w-3 h-3 text-yellow-600" data-id="element-4700" />}
                            <span className={`text-xs font-medium ${play.status === 'running' ? 'text-green-600' : 'text-yellow-600'}`} data-id="element-4701">
                              {play.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3" data-id="element-4702">
                          <p className="text-sm text-gray-900" data-id="element-4703">
                            {play.accounts}
                          </p>
                        </td>
                        <td className="px-4 py-3" data-id="element-4704">
                          <p className="text-sm text-gray-900" data-id="element-4705">
                            {play.touchpoints.toLocaleString()}
                          </p>
                        </td>
                        <td className="px-4 py-3" data-id="element-4706">
                          <p className="text-sm font-medium text-gray-900" data-id="element-4707">
                            {play.responseRate}
                          </p>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
            {/* AI Insights with shimmer effect */}
            <div className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-300" data-id="element-4708">
              <div className="px-4 py-3 border-b border-gray-200" data-id="element-4709">
                <div className="flex items-center gap-2" data-id="element-4710">
                  <BrainIcon className="w-4 h-4 text-gray-600" data-id="element-4711" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-4712">
                    AI Insights & Recommendations
                  </h3>
                </div>
              </div>
              <div className="p-4 space-y-3" data-id="element-4713">
                {aiInsights.map((insight, index) => <div key={index} className={`p-3 rounded-lg border hover:shadow-sm transition-all duration-300 ${insight.type === 'opportunity' ? 'bg-blue-50 border-blue-200' : insight.type === 'warning' ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`} data-id="element-4714">
                    <div className="flex items-start gap-3" data-id="element-4715">
                      {insight.type === 'opportunity' ? <TargetIcon className="w-4 h-4 text-blue-600 mt-0.5" data-id="element-4716" /> : insight.type === 'warning' ? <AlertTriangleIcon className="w-4 h-4 text-yellow-600 mt-0.5" data-id="element-4717" /> : <CheckCircleIcon className="w-4 h-4 text-green-600 mt-0.5" data-id="element-4718" />}
                      <div className="flex-1" data-id="element-4719">
                        <p className={`text-sm font-medium mb-1 ${insight.type === 'opportunity' ? 'text-blue-900' : insight.type === 'warning' ? 'text-yellow-900' : 'text-green-900'}`} data-id="element-4720">
                          {insight.message}
                        </p>
                        <p className={`text-xs ${insight.type === 'opportunity' ? 'text-blue-700' : insight.type === 'warning' ? 'text-yellow-700' : 'text-green-700'}`} data-id="element-4721">
                          Recommended: {insight.action}
                        </p>
                      </div>
                      <button className={`text-xs font-medium px-3 py-1 rounded hover:opacity-90 transition-opacity ${insight.type === 'opportunity' ? 'bg-blue-600 text-white' : insight.type === 'warning' ? 'bg-yellow-600 text-white' : 'bg-green-600 text-white'}`} data-id="element-4722">
                        Act
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Right Column - 3 cols */}
          <div className="col-span-3" data-id="element-4723">
            <div className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-300" data-id="element-4724">
              {/* Tabs */}
              <div className="border-b border-gray-200" data-id="element-4725">
                <div className="flex" data-id="element-4726">
                  <button onClick={() => setActiveTab('executive')} className={`flex-1 px-4 py-3 text-xs font-medium border-b-2 transition-all duration-200 ${activeTab === 'executive' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-4727">
                    <div className="flex items-center justify-center gap-2" data-id="element-4728">
                      <TrendingUpIcon className="w-4 h-4" data-id="element-4729" />
                      Executive View
                    </div>
                  </button>
                  <button onClick={() => setActiveTab('activity')} className={`flex-1 px-4 py-3 text-xs font-medium border-b-2 transition-all duration-200 ${activeTab === 'activity' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-4730">
                    <div className="flex items-center justify-center gap-2" data-id="element-4731">
                      <ActivityIcon className="w-4 h-4" data-id="element-4732" />
                      Activity Feed
                    </div>
                  </button>
                </div>
              </div>
              {/* Tab Content */}
              <div className="p-4" data-id="element-4733">
                {activeTab === 'executive' ? <div className="space-y-4" data-id="element-4734">
                    <div data-id="element-4735">
                      <h4 className="text-xs font-semibold text-gray-900 mb-3" data-id="element-4736">
                        Strategic Initiatives
                      </h4>
                      <div className="space-y-2" data-id="element-4737">
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 hover:shadow-sm transition-shadow" data-id="element-4738">
                          <div className="flex items-center justify-between mb-2" data-id="element-4739">
                            <p className="text-sm font-medium text-blue-900" data-id="element-4740">
                              Enterprise Expansion
                            </p>
                            <span className="text-xs font-medium text-blue-700 px-2 py-0.5 bg-blue-100 rounded-full" data-id="element-4741">
                              On Track
                            </span>
                          </div>
                          <p className="text-xs text-blue-700 mb-2" data-id="element-4742">
                            Target: $5M pipeline by Q1 end
                          </p>
                          <div className="w-full bg-blue-100 rounded-full h-1.5" data-id="element-4743">
                            <div className="bg-blue-600 h-1.5 rounded-full transition-all duration-500" style={{
                          width: '68%'
                        }} data-id="element-4744" />
                          </div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200 hover:shadow-sm transition-shadow" data-id="element-4745">
                          <div className="flex items-center justify-between mb-2" data-id="element-4746">
                            <p className="text-sm font-medium text-green-900" data-id="element-4747">
                              PLG Motion Launch
                            </p>
                            <span className="text-xs font-medium text-green-700 px-2 py-0.5 bg-green-100 rounded-full" data-id="element-4748">
                              Ahead
                            </span>
                          </div>
                          <p className="text-xs text-green-700 mb-2" data-id="element-4749">
                            Target: 500 trial signups
                          </p>
                          <div className="w-full bg-green-100 rounded-full h-1.5" data-id="element-4750">
                            <div className="bg-green-600 h-1.5 rounded-full transition-all duration-500" style={{
                          width: '92%'
                        }} data-id="element-4751" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-4752">
                      <h4 className="text-xs font-semibold text-gray-900 mb-3" data-id="element-4753">
                        Key Metrics Trends
                      </h4>
                      <div className="grid grid-cols-2 gap-3" data-id="element-4754">
                        <div className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-4755">
                          <p className="text-xs text-gray-600 mb-1" data-id="element-4756">
                            Conversion Rate
                          </p>
                          <p className="text-xl font-bold text-gray-900" data-id="element-4757">
                            24.5%
                          </p>
                          <p className="text-xs text-green-600 mt-1" data-id="element-4758">
                            +3.2% vs last month
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-4759">
                          <p className="text-xs text-gray-600 mb-1" data-id="element-4760">Win Rate</p>
                          <p className="text-xl font-bold text-gray-900" data-id="element-4761">32%</p>
                          <p className="text-xs text-green-600 mt-1" data-id="element-4762">
                            +5% vs last month
                          </p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-4763">
                      <h4 className="text-xs font-semibold text-gray-900 mb-3" data-id="element-4764">
                        Budget Utilization
                      </h4>
                      <div className="space-y-2" data-id="element-4765">
                        <div data-id="element-4766">
                          <div className="flex items-center justify-between text-xs mb-1" data-id="element-4767">
                            <span className="text-gray-600" data-id="element-4768">
                              Q1 Budget Used
                            </span>
                            <span className="font-medium text-gray-900" data-id="element-4769">
                              $730K / $1M
                            </span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-4770">
                            <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{
                          width: '73%'
                        }} data-id="element-4771" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> : <div className="space-y-3" data-id="element-4772">
                    {activityFeed.map((activity, index) => {
                  const Icon = activity.icon;
                  return <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-4773">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${activity.color}-100`} data-id="element-4774">
                            <Icon className={`w-4 h-4 text-${activity.color}-600`} data-id="element-4775" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-4776">
                            <p className="text-sm font-medium text-gray-900 mb-0.5" data-id="element-4777">
                              {activity.action}
                            </p>
                            <p className="text-xs text-gray-600 mb-1" data-id="element-4778">
                              {activity.details}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-4779">
                              {activity.time}
                            </p>
                          </div>
                        </div>;
                })}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* CSS Animations */}
      <style data-id="element-4780">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out;
        }
        .animate-fade-in-slow {
          animation: fadeIn 0.8s ease-out;
        }
        .animate-pulse-slow {
          animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: .7;
          }
        }
      `}</style>
    </div>;
}